import type { ReactNode } from 'react';

/**
 * G-2 — Callout primitive.
 *
 * Three tones: Tip, Warning, Example. Used inline in the moderator guide
 * via GitHub-style `> [!TIP]` / `> [!WARNING]` / `> [!EXAMPLE]` syntax
 * which the marked extension at `src/lib/guide-callout-extension.ts`
 * converts into matching HTML. The same shape is also exported as a
 * React component so workbench code (e.g. GuidePage demo blocks) can
 * compose Callouts without going through marked.
 *
 * Accessibility:
 *  - Tip uses role="note" (informational aside).
 *  - Warning uses role="alert" so screen readers announce it eagerly.
 *  - Example uses role="region" with an accessible name from the
 *    heading so navigating by landmark surfaces it.
 *
 * Visual language matches Dialog/Popover/Toast: bordered surface,
 * radius-md, shadow-sm, --space-* paddings.
 */

export type CalloutVariant = 'tip' | 'warning' | 'example';

interface CalloutProps {
  variant: CalloutVariant;
  /** Optional heading override; defaults to "Tip" / "Warning" / "Example". */
  title?: string;
  children: ReactNode;
}

const DEFAULT_TITLE: Record<CalloutVariant, string> = {
  tip: 'Tip',
  warning: 'Warning',
  example: 'Example',
};

const ROLE: Record<CalloutVariant, 'note' | 'alert' | 'region'> = {
  tip: 'note',
  warning: 'alert',
  example: 'region',
};

function CalloutIcon({ variant }: { variant: CalloutVariant }) {
  // Inline SVGs match the AiSidebarSummary icon style (currentColor strokes,
  // small viewbox) so the chip+callout family feels consistent.
  if (variant === 'tip') {
    return (
      <svg
        className="callout__icon"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M8 1.5a4.5 4.5 0 0 0-2.5 8.25V12a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1V9.75A4.5 4.5 0 0 0 8 1.5z"
          stroke="currentColor"
          strokeWidth="1.4"
          fill="none"
          strokeLinejoin="round"
        />
        <line x1="6.5" y1="14.5" x2="9.5" y2="14.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    );
  }
  if (variant === 'warning') {
    return (
      <svg
        className="callout__icon"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M8 1.5 14.5 13H1.5L8 1.5z"
          stroke="currentColor"
          strokeWidth="1.4"
          fill="none"
          strokeLinejoin="round"
        />
        <line x1="8" y1="6" x2="8" y2="9.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        <circle cx="8" cy="11.25" r="0.75" fill="currentColor" />
      </svg>
    );
  }
  // example
  return (
    <svg
      className="callout__icon"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="2" y="3" width="12" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.4" fill="none" />
      <line x1="4.5" y1="6" x2="11.5" y2="6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <line x1="4.5" y1="8.5" x2="9" y2="8.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <line x1="4.5" y1="11" x2="8" y2="11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

export default function Callout({ variant, title, children }: CalloutProps) {
  const heading = title || DEFAULT_TITLE[variant];
  const role = ROLE[variant];
  const labelId = `callout-${variant}-${Math.random().toString(36).slice(2, 7)}`;
  return (
    <aside
      className={`callout callout--${variant}`}
      role={role}
      aria-labelledby={role === 'region' ? labelId : undefined}
      data-variant={variant}
      data-testid={`callout-${variant}`}
    >
      <div className="callout__header">
        <CalloutIcon variant={variant} />
        <span className="callout__title" id={labelId}>
          {heading}
        </span>
      </div>
      <div className="callout__body">{children}</div>
    </aside>
  );
}
